import { Box, Button, Grid, Grid2, IconButton, MenuItem, Paper, Select, Snackbar, Switch, Table, TableBody, TableCell, TableSortLabel, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useEffect, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { makeGetRequestToUrl, makePostRequestToUrl } from '../commonFunc.jsx';
import config from '../../config/config.js';
import { Label } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import TokenError from "../tokenError";
import prevIcon from '../../assets/icons/left-arrow.svg';
import nextIcon from '../../assets/icons/right-arrow.svg';

const GET_DOCS_ENDPOINT = '/merchant/get_required_documents';
const SAVE_DOCS_ENDPOINT = '/merchant/add_update_merchant_required_doc';

const NAME_MAX_LENGTH = 64;
const MAX_IMAGES = 2;

const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42, height: 26, padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0, margin: 2, transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)', color: '#fff',
            '& + .MuiSwitch-track': { backgroundColor: '#65C466', opacity: 1, border: 0 },
        },
    },
    '& .MuiSwitch-thumb': { width: 22, height: 22 },
    '& .MuiSwitch-track': {
        borderRadius: 13, backgroundColor: '#E9E9EA', opacity: 1,
        transition: theme.transitions.create(['background-color'], { duration: 500 }),
    },
}));

const labelStyle = { fontSize: '16px', fontWeight: '500', color: '#83888E' };
const inputStyle = { height: '56px', fontSize: '16px', padding: '8px 14px', backgroundColor: '#FFFFFF' };
const paperStyle = { padding: '30px', maxWidth: '800px', margin: '20px auto', backgroundColor: '#FFFFFF', borderRadius: '12px' };
const sectionTitleStyle = { marginBottom: '20px', marginTop: '0px', fontSize: '20px', color: '#1A1E25' };

const ToggleRow = ({ label, checked, onChange }) => (
    <Grid item xs={12}>
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: '8px', backgroundColor: '#FFFFFF',
        }}>
            <Typography variant="body1" style={{ color: '#4A4A4A' }}>{label}</Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" style={{ marginRight: '8px', color: checked ? '#4A4A4A' : '#9E9E9E' }}>
                    {checked ? 'Yes' : 'No'}
                </Typography>
                <IOSSwitch checked={checked} onChange={(e) => onChange(e.target.checked)} />
            </div>
        </div>
    </Grid>
);

const MerchantReqDocs = () => {
    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;
    const navigate = useNavigate();

    // ---------- list state ----------
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('document_id');
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;

    // ---------- form state ----------
    const [open, setOpen] = useState(false);
    const [editable, setEditable] = useState(false);
    const [documentId, setDocumentId] = useState(0);
    const [documentName, setDocumentName] = useState('');
    const [noOfImages, setNoOfImages] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [documentRequired, setDocumentRequired] = useState(false);
    const [documentNumRequired, setDocumentNumRequired] = useState(false);

    // ---------- field errors ----------
    const [nameError, setNameError] = useState('');
    const [imagesError, setImagesError] = useState('');

    // ---------- misc ----------
    const [message, setMessage] = useState('');
    const [snackbar, showSnackbar] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [saving, setSaving] = useState(false);

    const notify = (msg) => {
        setMessage(typeof msg === 'string' ? msg : 'Something went wrong');
        showSnackbar(true);
    };

    const handleTokenExpired = () => {
        console.log('Token is expired. Please login again');
        localStorage.removeItem('token');
        setTokenExpired(true);
        setTimeout(() => navigate('/signup'), 2000);
    };

    const clearErrors = () => {
        setNameError('');
        setImagesError('');
    };

    const clearForm = () => {
        setDocumentId(0);
        setDocumentName('');
        setNoOfImages('');
        setIsActive(true);
        setDocumentRequired(false);
        setDocumentNumRequired(false);
        clearErrors();
    };

    // ---------- API: fetch ----------
    const fetchMerchantDocs = async () => {
        try {
            const token = localStorage.getItem('token');
            const url = serverUrl + GET_DOCS_ENDPOINT;
            const response = await makeGetRequestToUrl(url, { token, timestamp: new Date().getTime() });

            if (response.flag == 102) { handleTokenExpired(); return; }
            setRows(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.log('error', error);
            setRows([]);
        }
    };

    useEffect(() => {
        fetchMerchantDocs();
    }, []);

    // ---------- open / close ----------
    const handleOpenAdd = () => {
        clearForm();
        setEditable(false);
        setOpen(true);
    };

    const handleOpenEdit = (row) => {
        clearErrors();
        setDocumentId(row.document_id);
        setDocumentName(row.document_name || '');
        setNoOfImages(row.no_of_images != null ? String(row.no_of_images) : '');
        setIsActive(row.is_active === 1);
        setDocumentRequired(row.is_required === 1);
        setDocumentNumRequired(row.is_doc_number_required === 1);
        setEditable(true);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditable(false);
    };

    const handleReset = () => {
        if (editable) {
            const keepId = documentId; // keep the record being edited
            clearForm();
            setDocumentId(keepId);
        } else {
            clearForm();
        }
    };

    // ---------- API: save ----------
    const handleSubmit = async () => {
        if (saving) return;
        const token = localStorage.getItem('token');
        const name = documentName.trim();
        const images = Number(noOfImages);

        // Validation (mirrors the backend Joi schema)
        if (!name) {
            setNameError('Please enter a document name'); notify('Please enter a document name'); return;
        }
        if (name.length > NAME_MAX_LENGTH) {
            setNameError(`Maximum ${NAME_MAX_LENGTH} characters`); notify(`Document name can be at most ${NAME_MAX_LENGTH} characters`); return;
        }
        if (noOfImages === '' || !Number.isInteger(images) || images < 0 || images > MAX_IMAGES) {
            setImagesError(`Enter a whole number from 0 to ${MAX_IMAGES}`); notify(`Number of images must be between 0 and ${MAX_IMAGES}`); return;
        }
        if (images === 0 && !documentNumRequired) {
            setImagesError('With 0 images, turn on "Document Number Required" so the merchant has something to submit');
            notify('A document needs at least one image or a document number');
            return;
        }

        const payload = {
            token,
            document_name: name,
            no_of_images: images,
            is_doc_number_required: documentNumRequired ? 1 : 0,
            is_required: documentRequired ? 1 : 0,
            is_active: isActive ? 1 : 0,
        };
        if (editable && documentId) payload.document_id = documentId;

        setSaving(true);
        try {
            const url = operationsUrl + SAVE_DOCS_ENDPOINT;
            const response = await makePostRequestToUrl(url, payload);

            if (response.flag == 102) { handleTokenExpired(); return; }

            if (response.flag === 200) {
                notify(response.message || 'Document saved successfully');
                handleClose();
                fetchMerchantDocs();
            } else {
                notify(response.message || 'Failed to save document');
            }
        } catch (error) {
            console.error('Error while saving merchant document:', error);
            notify('Something went wrong while saving the document');
        } finally {
            setSaving(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        showSnackbar(false);
    };

    // ---------- sort / filter / paginate ----------
    const handleRequestSort = (property) => {
        const isAscending = orderBy === property && order === 'asc';
        setOrder(isAscending ? 'desc' : 'asc');
        setOrderBy(property);
        setRows((prev) => [...prev].sort((a, b) => {
            if (a[property] < b[property]) return isAscending ? 1 : -1;
            if (a[property] > b[property]) return isAscending ? -1 : 1;
            return 0;
        }));
    };

    const filteredRows = rows.filter((row) =>
        statusFilter === '' ? true : String(row.is_active) === statusFilter
    );
    const totalEntries = filteredRows.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage);
    const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <>
        <div>
            {tokenExpired && (<TokenError />)}

            <Typography style={{ fontSize: '28px', fontWeight: '500' }}>
                Merchant Documents
            </Typography>

            {/* Filters */}
            <Grid2 container spacing={2} alignItems="center" sx={{ marginTop: '2%', marginBottom: '2%' }}>
                <Grid2>
                    <Select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} displayEmpty
                        style={{ width: '220px', height: '50px', borderRadius: '8px', backgroundColor: statusFilter ? '#F9F9F9' : '#FFFFFF' }}>
                        <MenuItem value="">Status</MenuItem>
                        <MenuItem value="1">Active</MenuItem>
                        <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                </Grid2>

                <Grid2>
                    <Button
                        variant="contained" onClick={handleOpenAdd}
                        style={{ width: '185px', height: '50px', backgroundColor: '#000000', textTransform: 'none', borderRadius: '8px' }}>
                        + Add Document
                    </Button>
                </Grid2>
            </Grid2>

            {/* Add / Edit side panel */}
            {open && (
                <div style={{
                    position: 'fixed', top: 65, right: 0, height: 'calc(100vh - 65px)', width: '50%',
                    backgroundColor: '#f9f9f9', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)', zIndex: 1000,
                    display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: 20,
                }}>
                    <Box>
                        <div>
                            <IconButton aria-label="close" onClick={handleClose}
                                style={{ position: 'absolute', top: '15px', left: '10px', color: '#333', zIndex: 10 }}>
                                <CloseIcon style={{ fontSize: '32px', fontWeight: 'bold' }} />
                            </IconButton>
                            <Typography variant="h6" style={{ marginBottom: '0px', marginTop: '42px', fontSize: '24px', color: '#4E545D' }}>
                                {editable ? 'Edit Merchant Document' : 'Add Merchant Document'}
                            </Typography>
                        </div>

                        <div style={{ position: 'relative', padding: '20px 0 40px 0' }}>

                            {/* Status (edit only — new documents are created active) */}
                            {editable && (
                                <Paper elevation={3} style={{ ...paperStyle, margin: '0 auto 20px auto', padding: '16px 30px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography style={{ fontSize: '18px', color: '#1A1E25' }}>Document Status</Typography>
                                        <div style={{ display: 'flex', marginLeft: 'auto' }}>
                                            <Button variant="contained" onClick={() => setIsActive(true)}
                                                style={{
                                                    backgroundColor: isActive ? '#0DBA78' : '#F4F4F4', color: isActive ? '#FFFFFF' : '#9E9E9E',
                                                    textTransform: 'none', borderRadius: '8px 0 0 8px', boxShadow: 'none', minWidth: '80px',
                                                }}>
                                                Enable
                                            </Button>
                                            <Button variant="contained" onClick={() => setIsActive(false)}
                                                style={{
                                                    backgroundColor: !isActive ? '#D14545' : '#F4F4F4', color: !isActive ? '#FFFFFF' : '#9E9E9E',
                                                    textTransform: 'none', borderRadius: '0 8px 8px 0', boxShadow: 'none', minWidth: '80px',
                                                }}>
                                                Disable
                                            </Button>
                                        </div>
                                    </div>
                                </Paper>
                            )}

                            {/* Document Details */}
                            <Paper elevation={3} style={{ ...paperStyle, margin: '0 auto' }}>
                                <Typography variant="h6" style={sectionTitleStyle}>Document Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Label style={labelStyle}>Document Name<span style={{ color: 'red' }}>*</span></Label>
                                        <TextField sx={{ marginTop: '2%' }}
                                            placeholder="Document Name" fullWidth variant="outlined" type="text"
                                            value={documentName} error={Boolean(nameError)}
                                            helperText={nameError || `${documentName.length}/${NAME_MAX_LENGTH}`}
                                            onChange={(e) => { setDocumentName(e.target.value); setNameError(''); }}
                                            InputProps={{ inputProps: { maxLength: NAME_MAX_LENGTH }, style: inputStyle }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Label style={labelStyle}>Number Of Images<span style={{ color: 'red' }}>*</span></Label>
                                        <TextField sx={{ marginTop: '2%' }}
                                            placeholder={`0 to ${MAX_IMAGES}`} fullWidth variant="outlined" type="number"
                                            value={noOfImages} error={Boolean(imagesError)}
                                            helperText={imagesError || 'Use 0 if only a document number is needed'}
                                            onChange={(e) => { setNoOfImages(e.target.value); setImagesError(''); }}
                                            InputProps={{ inputProps: { min: 0, max: MAX_IMAGES, step: 1 }, style: inputStyle }}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Settings */}
                            <Paper elevation={3} style={paperStyle}>
                                <Typography variant="h6" style={sectionTitleStyle}>Document Settings</Typography>
                                <Grid container spacing={2}>
                                    <ToggleRow label="Document Required" checked={documentRequired} onChange={setDocumentRequired} />
                                    <ToggleRow
                                        label="Document Number Required"
                                        checked={documentNumRequired}
                                        onChange={(v) => { setDocumentNumRequired(v); setImagesError(''); }}
                                    />
                                </Grid>
                            </Paper>

                            <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'center', gap: '20px' }}>
                                <Button variant="contained" onClick={handleSubmit} disabled={saving}
                                    sx={{ width: '140px', height: '50px', textTransform: 'none', fontSize: '16px', backgroundColor: '#0065FF', color: 'white' }}>
                                    {saving ? 'Saving…' : editable ? 'Update' : 'Save'}
                                </Button>
                                <Button variant="contained" onClick={handleReset}
                                    sx={{ width: '140px', height: '50px', textTransform: 'none', fontSize: '16px', backgroundColor: '#F0F0F0', color: '#B0B0B0' }}>
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </Box>
                </div>
            )}

            {/* Table */}
            <TableContainer className="table-container" sx={{ maxHeight: '70vh' }}>
                <Table stickyHeader className="scrollable-table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" className="table-header">
                                <TableSortLabel active={orderBy === 'document_id'} direction={orderBy === 'document_id' ? order : 'asc'}
                                    onClick={() => handleRequestSort('document_id')}>
                                    Document ID
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" className="table-header">
                                <TableSortLabel active={orderBy === 'document_name'} direction={orderBy === 'document_name' ? order : 'asc'}
                                    onClick={() => handleRequestSort('document_name')}>
                                    Document Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" className="table-header">Number of Images</TableCell>
                            <TableCell align="center" className="table-header">Document Required</TableCell>
                            <TableCell align="center" className="table-header">Doc No. Required</TableCell>
                            <TableCell align="center" className="table-header">Status</TableCell>
                            <TableCell align="center" className="table-header">Action</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {pagedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body1">No data found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {pagedRows.map((row) => (
                            <TableRow key={row.document_id}>
                                <TableCell align="center"><Typography sx={{ minWidth: '100px' }}>{row.document_id ?? '--'}</Typography></TableCell>
                                <TableCell align="center"><Typography sx={{ minWidth: '150px' }}>{row.document_name || '--'}</Typography></TableCell>
                                <TableCell align="center"><Typography sx={{ minWidth: '80px' }}>{row.no_of_images ?? '--'}</Typography></TableCell>
                                <TableCell align="center"><Typography sx={{ minWidth: '80px' }}>{row.is_required === 1 ? 'Yes' : 'No'}</Typography></TableCell>
                                <TableCell align="center"><Typography sx={{ minWidth: '80px' }}>{row.is_doc_number_required === 1 ? 'Yes' : 'No'}</Typography></TableCell>
                                <TableCell align="center">
                                    <Typography sx={{ minWidth: '80px', fontWeight: '500', color: row.is_active === 1 ? '#0DBA78' : '#D14545' }}>
                                        {row.is_active === 1 ? 'Active' : 'Inactive'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Button
                                        variant="contained" disableRipple
                                        onClick={() => handleOpenEdit(row)}
                                        startIcon={
                                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M4.84304 9.45074L5.39054 6.89324C5.47025 6.53784 5.64979 6.21258 5.90804 5.95574L10.9105 0.998238C11.4178 0.481469 12.1066 0.182825 12.8305 0.165738C13.3765 0.157914 13.9031 0.368518 14.293 0.750738C15.1452 1.75759 15.0352 3.26115 14.0455 4.13324L9.04304 9.13574C8.7862 9.39399 8.46095 9.57353 8.10554 9.65324L5.54804 10.2007H5.40554C5.21858 10.2079 5.04 10.1228 4.92774 9.97316C4.81547 9.82347 4.78382 9.62822 4.84304 9.45074ZM6.70304 6.75824C6.59796 6.86007 6.52496 6.99043 6.49304 7.13324L6.12554 8.88074L7.87304 8.50574C8.01585 8.47382 8.14621 8.40082 8.24804 8.29574L13.2505 3.29324C13.7986 2.85451 13.9073 2.0636 13.498 1.49324C13.3188 1.32202 13.0783 1.23012 12.8305 1.23824C12.4035 1.25539 11.9999 1.43834 11.7055 1.74824L6.70304 6.75824Z" fill="currentColor"/>
                                                <path d="M13.933 6.19574C13.6241 6.19978 13.3746 6.44927 13.3705 6.75824V11.0257C13.3806 11.6289 13.148 12.2109 12.7249 12.641C12.3019 13.0711 11.7238 13.3133 11.1205 13.3132H3.97305C2.72607 13.2888 1.72688 12.2729 1.72305 11.0257V3.90824C1.74352 2.65955 2.7617 1.65807 4.01055 1.65824H8.27805C8.58871 1.65824 8.84055 1.4064 8.84055 1.09574C8.84055 0.785078 8.58871 0.533238 8.27805 0.533238H3.97305C2.10292 0.533125 0.581098 2.03823 0.560547 3.90824V11.0257C0.560547 12.9104 2.08838 14.4382 3.97305 14.4382H11.0905C12.9723 14.4341 14.4956 12.9075 14.4955 11.0257V6.75824C14.4915 6.44927 14.242 6.19978 13.933 6.19574Z" fill="currentColor"/>
                                            </svg>
                                        }
                                        sx={{
                                            backgroundColor: '#EFFBF7', color: '#0DBA78', border: '1px solid #0DBA78',
                                            textTransform: 'none', borderRadius: '8px', width: 140, height: 38,
                                            boxShadow: 'none', fontWeight: 600, transition: 'all 0.15s ease',
                                            '&:hover': { backgroundColor: '#0DBA78', color: '#fff', boxShadow: '0 2px 8px rgba(13,186,120,0.3)', transform: 'translateY(-1px)' },
                                            '&:active': { backgroundColor: '#0DBA78', color: '#fff', transform: 'translateY(0)' },
                                        }}
                                    >
                                        Edit
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="custom-pagination">
                    <div className="pagination-info">
                        {totalEntries === 0
                            ? 'Showing 0 entries'
                            : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, totalEntries)} of ${totalEntries} entries`}
                    </div>
                    <div className="pagination-controls">
                        <button onClick={() => setPage(page - 1)} disabled={page === 0} className="pagination-arrow">
                            <img src={prevIcon} alt="Previous" />
                        </button>
                        <div className="pagination-numbers">
                            {[...Array(totalPages)].map((_, index) => (
                                <button key={index} onClick={() => setPage(index)}
                                    className={`pagination-number ${page === index ? 'active' : ''}`}>
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className="pagination-arrow">
                            <img src={nextIcon} alt="Next" />
                        </button>
                    </div>
                </div>
            </TableContainer>

            <Snackbar open={snackbar} autoHideDuration={2000} onClose={handleSnackbarClose} message={message} />
        </div>
        </>
    );
};

export default MerchantReqDocs;